import { useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, FileText, Settings, Users } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAuth, hasRole } from "@/lib/auth";

const ROLE_LABEL = { admin: "Admin", manager: "Manager", staff: "Staff" };

export default function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;

  const initial = (user.name || user.email || "U").trim().charAt(0).toUpperCase();
  const role = user.role || "staff";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="w-9 h-9 rounded-full border border-white/15 overflow-hidden flex items-center justify-center bg-white/5 hover:bg-white/10 transition"
          data-testid="user-menu-trigger"
          aria-label="Account menu"
        >
          {user.picture ? (
            <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-sm font-semibold text-[#C5A880]">{initial}</span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-[#0A0C10] border-white/10 text-white w-60"
        data-testid="user-menu"
      >
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="font-medium truncate">{user.name || "Account"}</span>
          <span className="text-white/40 text-xs truncate">{user.email}</span>
          <span className="text-[#C5A880] text-[10px] uppercase tracking-widest mt-1">
            {ROLE_LABEL[role]}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem onClick={() => navigate("/dashboard")} className="cursor-pointer focus:bg-white/10" data-testid="menu-dashboard">
          <LayoutDashboard size={14} className="mr-2" /> Dashboard
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/history")} className="cursor-pointer focus:bg-white/10" data-testid="menu-history">
          <FileText size={14} className="mr-2" /> Quotations
        </DropdownMenuItem>
        {hasRole(role, "manager") && (
          <DropdownMenuItem onClick={() => navigate("/admin")} className="cursor-pointer focus:bg-white/10" data-testid="menu-settings">
            <Settings size={14} className="mr-2" /> Workspace settings
          </DropdownMenuItem>
        )}
        {hasRole(role, "admin") && (
          <DropdownMenuItem onClick={() => navigate("/team")} className="cursor-pointer focus:bg-white/10" data-testid="menu-team">
            <Users size={14} className="mr-2" /> Team
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem onClick={logout} className="cursor-pointer focus:bg-white/10 text-red-400" data-testid="menu-logout">
          <LogOut size={14} className="mr-2" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
